import type { Language } from '@eformulieren/engine'
import { type ReactNode, useId, useState } from 'react'
import { inLanguage } from '../language/text.js'
import { Dialog } from '../shell/Dialog.js'

/**
 * Een opmerking plaatsen bij geselecteerde tekst (R-73, besluit 12.41 van 1 november 2026).
 *
 * Een opmerking hangt aan een stuk tekst, zoals in de CitatenPOC (eisenboek 12.9): u selecteert tekst
 * in een tekstveld en kiest Opmerking. Het schrijven gebeurt in de gedeelde dialoog (`Dialog.tsx`) en
 * niet in een vak dat onder het veld openklapt; het gesprek staat daarna in het paneel Opmerkingen.
 *
 * **Wie het ziet, in één zin onder het tekstvak.** Een opmerking hangt in het sectiedocument, en wie
 * die sectie kan openen ziet haar: medeschrijvers, wie de sectie via delen leest, een adviseur met die
 * sectie, functioneel beheer, en NWO bij het nalezen van de ingediende versie. Er gaat geen melding
 * uit (U18). De zin is voor een schrijver en een meelezer dezelfde, want voor beiden is hij waar.
 */

const T = {
  titel: { nl: 'Opmerking plaatsen', en: 'Post a comment' },
  bij: { nl: 'Bij de tekst', en: 'On the text' },
  veld: { nl: 'Uw opmerking', en: 'Your comment' },
  wie: {
    nl: 'Iedereen die deze sectie kan openen ziet uw opmerking, ook na het indienen. Niemand krijgt er een melding van.',
    en: 'Everyone who can open this section sees your comment, also after submission. Nobody is notified.',
  },
  plaatsen: { nl: 'Plaatsen', en: 'Post' },
  bezig: { nl: 'Bezig met plaatsen…', en: 'Posting…' },
  annuleren: { nl: 'Annuleren', en: 'Cancel' },
}

export interface CommentDialogProps {
  readonly taal: Language
  /** De geselecteerde tekst. */
  readonly citaat: string
  /** Plaatsen; een belofte als het langs de pod gaat. De dialoog sluit pas als het gelukt is. */
  readonly onPlaatsen: (text: string) => unknown
  readonly onSluit: () => void
  /** Na het sluiten: terug naar de tekst. */
  readonly focusTerug?: () => void
}

export function CommentDialog({ taal: language, citaat: quote, onPlaatsen: onPlace, onSluit: onClose, focusTerug: focusBack }: CommentDialogProps): ReactNode {
  const [text, setText] = useState('')
  const fieldId = useId()
  const whoId = useId()
  const place = (): unknown => {
    const trimmed = text.trim()
    if (trimmed === '') return undefined
    return onPlace(trimmed)
  }
  return (
    <Dialog
      titel={inLanguage(T.titel, language)}
      taal={language}
      className="ef-opmerkingdialoog"
      primair={{ label: inLanguage(T.plaatsen, language), bezigTekst: inLanguage(T.bezig, language), uitgeschakeld: text.trim() === '', onActie: place }}
      secundair={{ label: inLanguage(T.annuleren, language) }}
      onSluit={onClose}
      {...(focusBack === undefined ? {} : { focusTerug: focusBack })}
    >
      <figure className="ef-opmerkingdialoog__citaat">
        <figcaption className="ef-opmerkingdialoog__bij">{inLanguage(T.bij, language)}</figcaption>
        <blockquote>{quote}</blockquote>
      </figure>
      <label className="ef-opmerkingdialoog__label" htmlFor={fieldId}>
        {inLanguage(T.veld, language)}
      </label>
      <textarea
        id={fieldId}
        className="ef-draad__antwoordveld"
        rows={4}
        data-autofocus
        aria-describedby={whoId}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <p id={whoId} className="ef-opmerkingdialoog__wie">
        {inLanguage(T.wie, language)}
      </p>
    </Dialog>
  )
}
